import React, { useState, useEffect } from "react";
import axios from "axios";
import { Button } from "@heroui/button";
import StockOwnershipSkeleton from "./StockOwnershipSkeleton"; // Import the skeleton component

const StockOwnershipTable = ({ ticker }) => {
  const [ownershipData, setOwnershipData] = useState([]);
  const [displayRows, setDisplayRows] = useState(10); // Initial display rows
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const response = await axios.post(
          "/api/company/stock-ownership",
          { ticker: ticker },
          {
            headers: {
              "Content-Type": "application/json",
              Accept: "application/json",
            },
          }
        );
        setOwnershipData(response.data);
      } catch (error) {
        setError("Failed to fetch stock ownership data");
        setOwnershipData([]);
      }
      setLoading(false);
    };

    if (ticker) {
      fetchData();
    }
  }, [ticker]);

  // Function to load more rows
  const loadMore = () => {
    // Increase displayRows by 10, but not beyond the total number of rows
    setDisplayRows((prevRows) => (prevRows >= 20 ? ownershipData.length : 20));
  };

  // Function to go back to showing the first 10 rows
  const goBack = () => {
    setDisplayRows(10); // Reset to show only the first 10 rows
  };

  if (loading) {
    return <StockOwnershipSkeleton />; // Show the skeleton loader while loading
  }

  if (ownershipData === "nodata") {
    return <div>No ownership data available for this company.</div>;
  }

  if (error) {
    return <div>{error}</div>;
  }

  return (
    <>
      {ownershipData.length > 0 && (
        <section
          id="ownership"
          className="p-5 bg-white shadow-md rounded-lg border mb-24 md:mb-8"
        >
          <h2 className="text-xl font-semibold mb-4">
            Institutional Ownership
          </h2>
          <div className="overflow-x-auto">
            <table className="w-full min-w-[600px]">
              <thead>
                <tr>
                  <th className="text-left pl-2">Investor Name</th>
                  <th className="text-center">Shares Number</th>
                  <th>Market Value</th>
                  <th>Ownership%</th>
                </tr>
              </thead>
              <tbody>
                {ownershipData.slice(0, displayRows).map((item, index) => (
                  <tr
                    key={index}
                    className={`${
                      index % 2 === 0 ? "bg-E6E6E6 h-8 rounded-sm" : ""
                    }`}
                  >
                    <td className="text-sm p-1 pl-2">{item.investorName}</td>
                    <td className="text-sm text-center">{item.sharesNumber}</td>
                    <td className="text-sm text-center">${item.marketValue}</td>
                    <td className="text-sm text-center">{item.ownership}%</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {displayRows < ownershipData.length && (
            <Button
              size="sm"
              className="mt-4 mr-2 px-4 py-2 bg-1B51CA text-white rounded hover:bg-blue-600 transition duration-300"
              onClick={loadMore}
            >
              Load More
            </Button>
          )}
          {displayRows > 10 && (
            <Button
              size="sm"
              className="mt-4 px-4 py-2 bg-red-500 text-white rounded hover:bg-red-600 transition duration-300"
              onClick={goBack}
            >
              Go Back
            </Button>
          )}
        </section>
      )}
    </>
  );
};

export default StockOwnershipTable;
